import React from 'react';
import { Resource, Title, useRedirect } from 'react-admin';
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@material-ui/core';
import AccountFeaturesEditor, {
  accessLabel,
  adminFetch,
  featureLabel,
  useAdminToken,
} from './AccountFeaturesEditor';

export const RESOURCE_NAME = 'account-features-overview';
export const PAGE_SIZE_OPTIONS = [25, 50, 100];

export const buildOverviewPath = ({
  accountId,
  featureKey,
  access,
  status,
  page,
  limit,
}) => {
  const params = new URLSearchParams();
  if (accountId) params.set('accountId', accountId);
  if (featureKey) params.set('featureKey', featureKey);
  if (access) params.set('access', access);
  if (status) params.set('status', status);
  params.set('page', String(page));
  params.set('limit', String(limit));
  return `/adminAccountFeatures?${params.toString()}`;
};

const filterSelectProps = (label) => ({
  select: true,
  label,
  SelectProps: { native: true },
  InputLabelProps: { shrink: true },
  inputProps: { 'aria-label': label },
  style: { minWidth: 220 },
});

/**
 * Lists every account with the features it has enabled (by `app_feature.key`).
 * Backed by the admin-only `GET /adminAccountFeatures` endpoint, which never
 * returns account secrets. Clicking a row opens the account to edit it.
 */
export const AccountFeaturesOverviewPage = () => {
  const token = useAdminToken();
  const redirect = useRedirect();

  const [accounts, setAccounts] = React.useState([]);
  const [features, setFeatures] = React.useState([]);
  const [statuses, setStatuses] = React.useState([]);
  const [optionsError, setOptionsError] = React.useState(null);

  const [accountId, setAccountId] = React.useState('');
  const [featureKey, setFeatureKey] = React.useState('');
  const [access, setAccess] = React.useState('');
  const [status, setStatus] = React.useState('');
  const [page, setPage] = React.useState(0);
  const [limit, setLimit] = React.useState(PAGE_SIZE_OPTIONS[0]);

  const [rows, setRows] = React.useState([]);
  const [total, setTotal] = React.useState(0);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState(null);

  React.useEffect(() => {
    let active = true;
    Promise.all([
      adminFetch('/adminAccountFeatures/accounts', token),
      adminFetch('/features', token),
      adminFetch('/adminAccountFeatures/statuses', token),
    ])
      .then(([accountList, featureList, statusList]) => {
        if (!active) return;
        const featureRows = Array.isArray(featureList)
          ? featureList
          : featureList?.data || [];
        setAccounts(accountList || []);
        setStatuses(statusList || []);
        setFeatures(
          [...featureRows].sort((a, b) =>
            String(a.name).localeCompare(String(b.name)),
          ),
        );
      })
      .catch((err) => active && setOptionsError(err.message));
    return () => {
      active = false;
    };
  }, [token]);

  React.useEffect(() => {
    let active = true;
    setLoading(true);
    setError(null);
    adminFetch(
      buildOverviewPath({
        accountId,
        featureKey,
        access,
        status,
        page: page + 1,
        limit,
      }),
      token,
    )
      .then((result) => {
        if (!active) return;
        setRows(result.data || []);
        setTotal(result.total || 0);
      })
      .catch((err) => {
        if (!active) return;
        setRows([]);
        setTotal(0);
        setError(err.message);
      })
      .finally(() => active && setLoading(false));
    return () => {
      active = false;
    };
  }, [token, accountId, featureKey, access, status, page, limit]);

  const onFilterChange = (setter) => (event) => {
    setter(event.target.value);
    setPage(0);
  };

  return (
    <>
      <Title title="Features by account" />
      <Card>
        <CardContent>
          <Box
            display="flex"
            flexWrap="wrap"
            style={{ gap: 16 }}
            marginBottom={2}
          >
            <TextField
              {...filterSelectProps('Account')}
              value={accountId}
              onChange={onFilterChange(setAccountId)}
              style={{ minWidth: 280 }}
            >
              <option value="">All accounts</option>
              {accounts.map((account) => (
                <option key={account.id} value={account.id}>
                  {account.name}
                  {account.inactive ? ' (disabled)' : ''}
                </option>
              ))}
            </TextField>
            <TextField
              {...filterSelectProps('Feature')}
              value={featureKey}
              onChange={onFilterChange(setFeatureKey)}
              style={{ minWidth: 280 }}
            >
              <option value="">All features</option>
              {features.map((feature) => (
                <option key={feature.key} value={feature.key}>
                  {featureLabel(feature.name)} ({feature.key})
                </option>
              ))}
            </TextField>
            <TextField
              {...filterSelectProps('Access')}
              value={access}
              onChange={onFilterChange(setAccess)}
            >
              <option value="">All</option>
              <option value="enabled">Enabled</option>
              <option value="disabled">Disabled</option>
            </TextField>
            <TextField
              {...filterSelectProps('Status')}
              value={status}
              onChange={onFilterChange(setStatus)}
            >
              <option value="">All statuses</option>
              {statuses.map((value) => (
                <option key={value} value={value}>
                  {value}
                </option>
              ))}
            </TextField>
          </Box>

          {optionsError && (
            <Typography color="error" variant="body2">
              Unable to load filter options: {optionsError}
            </Typography>
          )}
          {error && (
            <Typography color="error" variant="body2" role="alert">
              Unable to load account features: {error}
            </Typography>
          )}

          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Account</TableCell>
                <TableCell>
                  <Tooltip title="Disabled accounts cannot log in and their API and DPP stop serving">
                    <span>Access</span>
                  </Tooltip>
                </TableCell>
                <TableCell>
                  <Tooltip title="Commercial classification (app_account.status). It does not change access.">
                    <span>Status</span>
                  </Tooltip>
                </TableCell>
                <TableCell>Features</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading && (
                <TableRow>
                  <TableCell colSpan={4}>Loading...</TableCell>
                </TableRow>
              )}
              {!loading && !error && rows.length === 0 && (
                <TableRow>
                  <TableCell colSpan={4}>
                    No accounts match the filters.
                  </TableCell>
                </TableRow>
              )}
              {!loading &&
                rows.map((row) => (
                  <TableRow
                    key={row.id}
                    hover
                    style={{ cursor: 'pointer' }}
                    onClick={() => redirect(`/${RESOURCE_NAME}/${row.id}/show`)}
                    data-testid="account-features-row"
                  >
                    <TableCell>{row.name}</TableCell>
                    <TableCell>{accessLabel(row.inactive)}</TableCell>
                    <TableCell>{row.status || '—'}</TableCell>
                    <TableCell>
                      {row.features.length === 0 ? (
                        <Typography variant="body2" color="textSecondary">
                          No features
                        </Typography>
                      ) : (
                        <Box display="flex" flexWrap="wrap" style={{ gap: 4 }}>
                          {row.features.map((feature) => (
                            <Tooltip key={feature.key} title={feature.key}>
                              <Chip
                                size="small"
                                label={featureLabel(feature.name)}
                                color={
                                  feature.key === featureKey
                                    ? 'primary'
                                    : 'default'
                                }
                              />
                            </Tooltip>
                          ))}
                        </Box>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
            </TableBody>
          </Table>
          <TablePagination
            component="div"
            count={total}
            page={page}
            rowsPerPage={limit}
            rowsPerPageOptions={PAGE_SIZE_OPTIONS}
            onPageChange={(_, nextPage) => setPage(nextPage)}
            onRowsPerPageChange={(event) => {
              setLimit(Number(event.target.value));
              setPage(0);
            }}
          />
        </CardContent>
      </Card>
    </>
  );
};

/** Detail of one account (route `/account-features-overview/:id/show`). */
export const AccountFeaturesOverviewShow = ({ id }) => {
  const redirect = useRedirect();
  return (
    <>
      <Title title="Features by account" />
      <Card>
        <CardContent>
          <Button
            size="small"
            onClick={() => redirect(`/${RESOURCE_NAME}`)}
            style={{ marginBottom: 8 }}
          >
            ← Back to list
          </Button>
          <AccountFeaturesEditor accountId={id} />
        </CardContent>
      </Card>
    </>
  );
};

const accountFeaturesOverviewResource = () => (
  <Resource
    name={RESOURCE_NAME}
    options={{ label: 'Features by account', menuGroup: 'Bcome' }}
    list={AccountFeaturesOverviewPage}
    show={AccountFeaturesOverviewShow}
  />
);

export default accountFeaturesOverviewResource;
